import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import CopyToClipboard from 'react-copy-to-clipboard';
import {
  Button,
  CircularProgress,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import PostAddIcon from '@mui/icons-material/PostAdd';
import RestartAltOutlinedIcon from '@mui/icons-material/RestartAltOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import UnarchiveOutlinedIcon from '@mui/icons-material/UnarchiveOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import ReplyAllTwoToneIcon from '@mui/icons-material/ReplyAllTwoTone';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import { store } from '../../Redux/store';
import { DELETE_CLIENT, getAllClients } from '../../Redux/Actions/StudentActions';
import DialogLayout from '../../Components/DialogLayout';
import ClientRepository from '../../Repositories/ClientRepository';
import CustomFeesRepository from '../../Repositories/CustomFeesRepository';
import TeachrContactRepository from '../../Repositories/TeachrContactRepository';
import Teachr_Repository from '../../Repositories/Teachr_Repository';
import { useTailwindBrand } from '../../Components/shared';
import Teachr from '../../Objects/models/Teachr';
import {
  getActiveBrandConfig,
  getActiveClientBrandConfig,
  getActivePrestationBrandConfig,
  // getPathClientBrandConfig,
  getUriAppBrandConfig,
} from '../../config/environment.config';
import ButtonField from '../../Components/Ui/ButtonField';
import { SHOW_ERROR, SHOW_SUCCESS, SHOW_WARNING } from '../../Redux/Reducers/MessageReducers';
import { resetDeclarationV2, updateDeclarationV2 } from '../../Redux/Reducers/DeclarationV2Reducers';
import { useTravelScenario } from '../DeclarationCourse/Travel/TravelModeSelector';
import { useSessionDescriptionCheck } from '../DeclarationCourse/useSessionDescriptionCheck';
import StudentInfoCard from './StudentInfoCard';
import { ClientDetailLocationState, getClientDetailPath, hasClientDetailPage, resolveClientRowFields } from './clientDetailNavigation';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ClientRowActionsProps {
  client: any;
  teachr: Teachr;
  dataDispatch: any;
  load: boolean
  setLoad: React.Dispatch<React.SetStateAction<boolean>>
  /**
   * 'row' : menu d'une ligne de la liste (par défaut).
   * 'page' : bouton « Actions » de la fiche client, sans l'entrée « Consulter ».
   */
  variant?: 'row' | 'page';
  /** Remplace le rafraîchissement de la liste (la fiche recharge ses propres données). */
  onUpdated?: () => void;
  /** Appelé après une suppression réussie (la fiche revient à la liste). */
  onDeleted?: () => void;
}

// ─── Composant ────────────────────────────────────────────────────────────────

const ClientRowActions: React.FC<ClientRowActionsProps> = ({ client, teachr, dataDispatch, load, setLoad, variant = 'row', onUpdated, onDeleted }) => {
  // const dispatch = useDispatch<AppDispatch>();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { brandConfig, classes } = useTailwindBrand();
  const isPageVariant = variant === 'page';

  // ── State ──────────────────────────────────────────────────────────────────
  const [typeCours, setTypeCours] = useState(false);
  const [isCopy, setIsCopy] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);

  // Dialogs
  const [openRelance, setOpenRelance] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [openArchive, setOpenArchive] = useState(false);
  const [openProfile, setOpenProfile] = useState(false);
  const [openDeclare, setOpenDeclare] = useState(false);

  // ── Résolution des champs : supporte ancien format snake_case et nouveau camelCase ──
  const {
    isPending,
    clientId,
    childId,
    firstName,
    lastName,
    isArchive,
    contactKey,
    customFeesId,
    fullName,
    dateTime,
  } = resolveClientRowFields(client);
  const pendingUrl = `${getUriAppBrandConfig()}/client/complete-infos/${contactKey}`;

  // ── Scénarios distance (P5) : attestation 60–160 km, preuve > 160 km ───────
  // La déclaration depuis « Mes élèves » saute FirstStep (step: 2) : le
  // contrôle distance doit donc se faire ici, avant la navigation.
  const { checkTravelScenario, travelModals } = useTravelScenario(teachr);
  // Description habituelle obligatoire avant de déclarer (contrôle de l'étape 1 du parcours v2, sautée ici)
  const { checkSessionDescription, descriptionModal } = useSessionDescriptionCheck(teachr);
  // Objet child normalisé (les lignes peuvent être en snake_case) : c'est lui
  // qui est stocké dans le state de déclaration et relu par LastStep.
  const childForDeclaration = {
    ...client,
    clientId,
    childId,
    // adresse du cours (celle de la ligne élève) : le scénario distance est décidé sur elle
    addressId: client?.addressId ?? client?.address_id ?? null,
    childFirstName: client?.childFirstName ?? client?.child_first_name ?? firstName,
    childLastName: client?.childLastName ?? client?.child_last_name ?? lastName,
  };

  // let timerInterval: any;

  useEffect(() => {
    if (isCopy) setTimeout(() => setIsCopy(false), 1000);
  }, [isCopy]);

  // ── Menu ──────────────────────────────────────────────────────────────────
  const handleOpenMenu = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const handleCloseMenu = () => setAnchorEl(null);

  // ── Consulter : fiche dédiée pour un client inscrit, popup sinon ──────────
  const handleConsult = () => {
    handleCloseMenu();
    if (hasClientDetailPage(client)) {
      const state: ClientDetailLocationState = { fromList: true };
      navigate(getClientDetailPath(clientId), { state });
      return;
    }
    setOpenProfile(true);
  };

  // ── Refresh (actif/archive) ────────────────────────────────────────────────
  const refreshLists = () => {
    // Sur la fiche client, c'est elle qui recharge ses données
    if (onUpdated) {
      onUpdated();
      return;
    }
    store.dispatch(getAllClients(dataDispatch));
  };

  // Après une suppression, la fiche client n'a plus lieu d'être : retour à la liste
  const afterDelete = () => {
    if (onDeleted) {
      onDeleted();
      return;
    }
    refreshLists();
  };

  const handleRefresh = async () => {
    setLoad(true);
    try {
      setLoad(true);
      if (teachr?.id) {
        const response = await ClientRepository.refreshStatusTdp(clientId, teachr?.id!);
        if (response) refreshLists();
        setLoad(false);
      }
    } catch (error: any) {
      dispatch({ type: 'SHOW_ERROR', payload: error });
      setLoad(false);
    }
    // finally {
    // }
  };

  // ── Déclarer (actif) ──────────────────────────────────────────────────────
  const handleToggleDeclare = () => {
    handleCloseMenu();
    if (!teachr?.iban) {
      navigate(`/${getActiveBrandConfig()}/tableau-de-bord/parametre`, { state: { step: 2 } });
      return;
    }
    setOpenDeclare(true);
  };

  const handleRefreshAndDeclare = async (cours: boolean) => {
    setLoad(true);
    setTypeCours(cours)
    try {
      if (teachr?.id) {
        const res = await ClientRepository.refreshStatusTdp(clientId, teachr?.id!);
        if (res) refreshLists();
        if (!client?.isTeachrContact && (!client?.pricePerHour || !client?.feesPerHour)) {
          dispatch({ type: SHOW_WARNING, payload: `Cet ajout est en attente de validation par un administrateur. Veuillez patienter environ deux jours pour la confirmation ou contactez le service client.` })
          setOpenDeclare(false);
          return;
        }

        if (res?.etat === 'OK') {
          // const dateTimeTdpMoment = moment(dateTime).format('DD/MM/YYYY HH:mm:ss');
          const currentMoment = moment();
          const canDeclare = currentMoment.format('DD/MM/YYYY') !== dateTime!.format('DD/MM/YYYY') || currentMoment.diff(dateTime, 'hours', true) > 2;

          if (canDeclare) {
            // Parcours de déclaration v2 : l'élève est déjà choisi, on arrive
            // directement à l'étape 2 (planning), avec les contrôles de l'étape 1
            // faits ici (statut d'avance immédiate, description habituelle, déplacement).
            checkSessionDescription(() => checkTravelScenario(childForDeclaration, () => {
              dispatch(resetDeclarationV2());
              dispatch(updateDeclarationV2({
                courseType: cours ? 'multiple' : 'single',
                student: {
                  childId: parseInt(childId),
                  child: childForDeclaration,
                  activationDate: dateTime ? dateTime.toISOString() : null,
                },
                sessionDescriptionOverride: null,
                step: 'planning',
                updatedAt: new Date().toISOString(),
              }));
              setOpenDeclare(false);
              navigate(`/${getActiveBrandConfig()}/tableau-de-bord/${getActivePrestationBrandConfig()}/declarer`, { state: { keepStep: true } });
            }));
          } else {
            const diffMinutes = currentMoment.diff(dateTime, 'minutes');
            const hours = Math.floor(diffMinutes / 60);
            const minutes = 60 - (diffMinutes % 60);
            const waitTime = `${hours > 0 ? hours : '01'}h${minutes < 10 ? `0${minutes}` : minutes}`;
            dispatch({ type: SHOW_WARNING, payload: `Vous ne pouvez pas déclarer de cours pour ${fullName} actuellement. Veuillez réessayer dans ${waitTime}.` })
            // Swal.fire({
            //   icon: 'warning',
            //   title: <div className={common.subtitle2}>Attention !</div>,
            //   html: <div className={common.body}>{`Vous ne pouvez pas déclarer de cours pour ${fullName} actuellement. Veuillez réessayer dans ${waitTime}.`}</div>,
            // });
            setOpenDeclare(false);
          }
        } else {
          dispatch({ type: SHOW_ERROR, payload: `Le compte de votre client ${fullName} a été bloqué pour le moment.` })
          // Swal.fire({
          //   icon: 'error',
          //   title: <div className={common.subtitle2}>Erreur</div>,
          //   html: <div className={common.body}>{`Le compte de votre client ${fullName} a été bloqué pour le moment.`}</div>,
          // });
          setOpenDeclare(false);
        }
      }

    } catch (error: any) {
      dispatch({ type: 'SHOW_ERROR', payload: error });
      setOpenDeclare(false);
    } finally {
      setLoad(false);
    }
  };

  // ── Archivage ─────────────────────────────────────────────────────────────
  const handleArchivage = async () => {
    try {
      setLoad(true);
      if (isArchive) {
        await CustomFeesRepository.remove_archives_client_by_teachr(customFeesId);
      } else {
        await CustomFeesRepository.archives_client_by_teachr(customFeesId);
      }
      refreshLists();
      setOpenArchive(false);
      dispatch({ type: SHOW_SUCCESS, payload: `Vous venez ${!isArchive ? "d'archiver" : 'de désarchiver'} ${getActiveClientBrandConfig('', 'cet ', 'ce ')}, vous pouvez le retrouver dans la liste ${!isArchive ? 'archivés' : `active des ${getActiveClientBrandConfig('s')}`}.` })
    } catch (err) {
      console.warn(err);
      dispatch({ type: SHOW_ERROR, payload: `Erreur inconnue, veuillez contacter le service client` })
      // Swal.fire({ icon: 'error', title: <div className={common.subtitle2}>Erreur</div>, html: <div className={common.body}>Erreur inconnue, veuillez contacter le service client.</div>, timer: 5000 });
      setOpenArchive(false);
    } finally {
      setLoad(false);
    }
  };

  // ── Suppression (actif/archive) ────────────────────────────────────────────
  const handleDeleteActive = async () => {
    try {
      setLoad(true);
      if (!clientId || !teachr?.id) return;
      await Teachr_Repository.removeTeachrFavoriteByTeachr(clientId, teachr.id);
      const fees = await CustomFeesRepository.getCustomFeesSingle(clientId, teachr.id);
      // deleteCustomFees attend la fiche tarifaire (il lit son id) : celle du couple
      // prof/client renvoyée par l'API, sinon celle de la ligne quand l'API n'en trouve aucune.
      const feesToDelete = Array.isArray(fees) && fees.length > 0
        ? fees[0]
        : (customFeesId ? { id: customFeesId } : null);
      if (feesToDelete?.id) await CustomFeesRepository.deleteCustomFees(feesToDelete);
      await handleStudentRemoval(teachr.id);
      setOpenDelete(false);
      afterDelete();
      dispatch({ type: SHOW_SUCCESS, payload: `Vous venez de supprimer ${getActiveClientBrandConfig('', 'cet ', 'ce ')} de votre compte.` })
      // Swal.fire({
      //   icon: 'success',
      //   title: <div className={common.subtitle2}>Félicitations !</div>,
      //   html: <div className={common.body}>{`Vous venez de supprimer ${getActiveClientBrandConfig('', 'cet ', 'ce ')} de votre compte.`}</div>,
      //   timer: 5000,
      //   willClose: () => navigate(`/${getActiveBrandConfig()}/tableau-de-bord/${getPathClientBrandConfig()}`),
      // });
    } catch (error) {
      console.error(error);
      setLoad(false);
    } finally {
      setLoad(false);
    }
  };

  const handleStudentRemoval = async (teachrId: number) => {
    try {
      const resultat = await TeachrContactRepository.fetchTeachrContactByMail2(teachrId, client?.mail);
      if (resultat.length > 0) await TeachrContactRepository.deleteTeachrContact(resultat[0].id);
      dispatch({ type: DELETE_CLIENT, payload: client?.mail.toLowerCase() })
    } catch {
      setLoad(false);
    }
  };

  // ── Suppression (pending) ─────────────────────────────────────────────────
  const handleDeletePending = async () => {
    setLoad(true);
    try {
      const response: any = await TeachrContactRepository.deleteTeachrContact(client.id);

      if (response) {
        dispatch({ type: DELETE_CLIENT, payload: response.mail.toLowerCase() })
      }
      setOpenDelete(false);
      afterDelete();
      dispatch({ type: SHOW_SUCCESS, payload: `Vous venez de supprimer cet ${getActiveClientBrandConfig()} de votre compte.` })
      // Swal.fire({
      //   icon: 'success',
      //   html: <div className={common.body}>{`Vous venez de supprimer cet ${getActiveClientBrandConfig()} de votre compte.`}</div>,
      //   timer: 5000,
      //   willClose: () => clearInterval(timerInterval),
      // });
    } catch {
      setLoad(false);
    } finally {
      setLoad(false);
    }
  };

  // ── Relance (pending) ─────────────────────────────────────────────────────
  const handleRelance = async (statut?: string) => {
    if (!teachr?.id) return;
    const response = await Teachr_Repository.sendMailToClient(teachr.id, client.id, statut, client.mail, fullName);
    setOpenRelance(false);
    if (response === 'phone number not found') {
      dispatch({ type: SHOW_SUCCESS, payload: `Ce client n'a pas de numéro de téléphone enregistré. Veuillez effectuer la relance par e-mail` })
      // Swal.fire({
      //   icon: 'warning',
      //   title: <div className={common.subtitle2}>Erreur : Numéro de Téléphone Manquant</div>,
      //   html: <div className={common.body}>Ce client n'a pas de numéro de téléphone enregistré. Veuillez effectuer la relance par e-mail.</div>,
      //   timer: 5000,
      //   willClose: () => clearInterval(timerInterval),
      // });
    } else {
      dispatch({ type: SHOW_SUCCESS, payload: `Vous venez d'envoyer un e-mail de relance à votre client.` })
      // Swal.fire({
      //   icon: 'success',
      //   html: <div className={common.body}>Vous venez d'envoyer un e-mail de relance à votre client.</div>,
      //   timer: 5000,
      //   willClose: () => clearInterval(timerInterval),
      // });
    }
  };

  // ── Fiche client : barre d'actions explicite ───────────────────────────────
  // Au plus trois boutons visibles, choisis selon le statut ; le reste
  // (archivage, suppression, lien d'inscription) dans le menu « ⋯ ».
  type PageAction = { key: string; label: string; icon: React.ReactNode; onClick: () => void; danger?: boolean; color?: 'primary' | 'warning' | 'error' | 'info' | 'success' };
  const canDeclareCourse = client?.status === 'APPAREILLAGE_VALIDE' && !isArchive;
  const canRelance = isPending || client?.status === 'CONTACT_COMPLETE' || client?.status === 'APPAREILLAGE_NF';
  const copyPendingLink = () => {
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(pendingUrl).then(() => setIsCopy(true)).catch(() => undefined);
    }
  };
  const declareAction: PageAction = { key: 'declare', label: `Déclarer ${getActivePrestationBrandConfig('', 'un ', 'une ')}`, icon: <PostAddIcon fontSize="small" />, onClick: handleToggleDeclare };
  const relanceAction: PageAction = { key: 'relance', label: 'Relancer', icon: <ReplyAllTwoToneIcon fontSize="small" />, onClick: () => setOpenRelance(true) };
  const refreshAction: PageAction = { key: 'refresh', label: 'Actualiser le statut', icon: <RestartAltOutlinedIcon fontSize="small" />, onClick: handleRefresh };
  const copyAction: PageAction = { key: 'copy', label: isCopy ? 'Lien copié ✓' : "Copier le lien d'inscription", icon: <ContentCopyIcon fontSize="small" />, onClick: copyPendingLink };
  const archiveAction: PageAction = isArchive
    ? { key: 'unarchive', label: 'Désarchiver', icon: <UnarchiveOutlinedIcon fontSize="small" />, onClick: () => setOpenArchive(true), color: 'info' }
    : { key: 'archive', label: 'Archiver', icon: <ArchiveOutlinedIcon fontSize="small" />, onClick: () => setOpenArchive(true), color: 'warning' };
  const deleteAction: PageAction = { key: 'delete', label: 'Supprimer', icon: <DeleteOutlinedIcon fontSize="small" />, onClick: () => setOpenDelete(true), danger: true };

  const primaryAction: PageAction | null = isPending ? relanceAction : canDeclareCourse ? declareAction : canRelance ? relanceAction : null;
  const secondaryActions: PageAction[] = isPending
    ? [copyAction]
    : [refreshAction, ...(canRelance && primaryAction?.key !== 'relance' ? [relanceAction] : [])];
  const overflowActions: PageAction[] = isPending ? [deleteAction] : [archiveAction, deleteAction];

  const overflowMenuItems = overflowActions.map((action, index) => (
    <MenuItem
      key={action.key}
      onClick={() => { handleCloseMenu(); action.onClick(); }}
      sx={action.danger ? { color: 'error.main' } : undefined}
      divider={action.danger && index > 0}
    >
      <ListItemIcon>{React.cloneElement(action.icon as React.ReactElement, { color: action.danger ? 'error' : (action.color ?? 'primary') })}</ListItemIcon>
      <ListItemText sx={{ fontFamily: brandConfig.fontFamily }} >
        <span className={classes.fontFamily} >{action.label}</span>
      </ListItemText>
    </MenuItem>
  ));

  // ── Rendu ─────────────────────────────────────────────────────────────────
  return (
    <>
      {travelModals}
      {descriptionModal}
      {/* ── Déclencheur ── */}
      {isPageVariant ? (
        <div className="flex flex-wrap items-center gap-2" role="toolbar" aria-label="Actions sur la fiche">
          {primaryAction && (
            <ButtonField
              size="small"
              variant="contained"
              label={primaryAction.label}
              startIcon={primaryAction.icon}
              loading={load}
              disabled={load}
              onClick={primaryAction.onClick}
            />
          )}
          {secondaryActions.map((action) => (
            <ButtonField
              key={action.key}
              size="small"
              variant="outlined"
              label={action.label}
              startIcon={action.icon}
              disabled={load}
              onClick={action.onClick}
            />
          ))}
          {overflowActions.length > 0 && (
            <Tooltip title="Plus d'actions">
              <IconButton
                size="small"
                onClick={handleOpenMenu}
                aria-label="Plus d'actions"
                aria-haspopup="true"
                sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, color: brandConfig.colors.primary }}
              >
                {load ? <CircularProgress size={16} /> : <MoreHorizIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          )}
        </div>
      ) : (
        <Tooltip title="Actions">
          <IconButton size="small" onClick={handleOpenMenu}>
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      {/* ── Menu ── */}
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleCloseMenu}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        
        slotProps={{ paper: { elevation: 3, sx: { minWidth: 180, borderRadius: 2, "& .MuiTypography-root": { fontFamily: brandConfig?.fontFamily } } } }}
      >
        {isPageVariant ? overflowMenuItems : isPending ? (
          [
            <MenuItem key="consult" onClick={handleConsult}>
              <ListItemIcon><RemoveRedEyeOutlinedIcon fontSize="small" color="primary" /></ListItemIcon>
              <ListItemText>Consulter</ListItemText>
            </MenuItem>,
            <MenuItem key="relance" onClick={() => { handleCloseMenu(); setOpenRelance(true); }}>
              <ListItemIcon><ReplyAllTwoToneIcon fontSize="small" color="primary" /></ListItemIcon>
              <ListItemText>Relancer</ListItemText>
            </MenuItem>,
            <MenuItem key="copy" onClick={() => { handleCloseMenu(); }}>
              <CopyToClipboard text={pendingUrl} onCopy={() => setIsCopy(true)}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
                  <ContentCopyIcon fontSize="small" color={isCopy ? 'success' : 'primary'} />
                  <span>{isCopy ? 'Copié ✓' : 'Copier le lien'}</span>
                </div>
              </CopyToClipboard>
            </MenuItem>,
            <Divider key="div" />,
            <MenuItem key="delete" onClick={() => { handleCloseMenu(); setOpenDelete(true); }} sx={{ color: 'error.main' }}>
              <ListItemIcon><DeleteOutlinedIcon fontSize="small" color="error" /></ListItemIcon>
              <ListItemText>Supprimer</ListItemText>
            </MenuItem>,
          ]
        ) : (
          [
            <MenuItem key="refresh" onClick={() => { handleCloseMenu(); handleRefresh(); }}>
              <ListItemIcon>
                {load ? <CircularProgress size={18} /> : <RestartAltOutlinedIcon fontSize="small" color="primary" />}
              </ListItemIcon>
              <ListItemText>Actualiser</ListItemText>
            </MenuItem>,
            client?.status === "APPAREILLAGE_VALIDE" && !isArchive ? (
              <MenuItem key="declare" onClick={handleToggleDeclare}>
                <ListItemIcon><SendOutlinedIcon fontSize="small" color="success" /></ListItemIcon>
                <ListItemText>Déclarer un cours</ListItemText>
              </MenuItem>
            ) : null,
            (client?.status === 'CONTACT_COMPLETE' || client?.status === 'APPAREILLAGE_NF') ? (
              <MenuItem key="relance" onClick={() => { handleCloseMenu(); setOpenRelance(true); }}>
                <ListItemIcon><ReplyAllTwoToneIcon fontSize="small" color="primary" /></ListItemIcon>
                <ListItemText>Relancer</ListItemText>
              </MenuItem>
            ) : null,
            !isPageVariant ? (
              <MenuItem key="view" onClick={handleConsult}>
                <ListItemIcon><RemoveRedEyeOutlinedIcon fontSize="small" color="primary" /></ListItemIcon>
                <ListItemText>Consulter</ListItemText>
              </MenuItem>
            ) : null,
            !isArchive ? (
              <MenuItem key="archive" onClick={() => { handleCloseMenu(); setOpenArchive(true); }}>
                <ListItemIcon><ArchiveOutlinedIcon fontSize="small" color="warning" /></ListItemIcon>
                <ListItemText sx={{ fontFamily: brandConfig.fontFamily }}>Archiver</ListItemText>
              </MenuItem>
            ) : (
              <MenuItem key="unarchive" onClick={() => { handleCloseMenu(); setOpenArchive(true); }}>
                <ListItemIcon><UnarchiveOutlinedIcon fontSize="small" color="info" /></ListItemIcon>
                <ListItemText sx={{ fontFamily: brandConfig.fontFamily }}>Désarchiver</ListItemText>
              </MenuItem>
            ),
            <Divider key="div" />,
            <MenuItem key="delete" onClick={() => { handleCloseMenu(); setOpenDelete(true); }} sx={{ color: 'error.main' }}>
              <ListItemIcon><DeleteOutlinedIcon fontSize="small" color="error" /></ListItemIcon>
              <ListItemText sx={{ fontFamily: brandConfig.fontFamily }}>Supprimer</ListItemText>
            </MenuItem>,
          ].filter(Boolean)
        )}
      </Menu>

      {/* ── Dialog : Relancer (pending / CONTACT_COMPLETE / APPAREILLAGE_NF) ── */}
      <DialogLayout
        title={`Relancer votre ${getActiveClientBrandConfig()}`}
        text={
          (client?.status === 'CONTACT_COMPLETE' || client?.status === 'APPAREILLAGE_NF') ? (
            <div className="space-y-3 text-sm text-gray-700">
              <p>
                Vous pouvez relancer <strong>{fullName}</strong> en lui envoyant un <strong>e-mail</strong> pour lui rappeler d'<strong>activer son compte URSSAF</strong>.
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs space-y-2">
                <p className="font-semibold">✔️ Ce que votre {getActiveClientBrandConfig()} doit faire</p>
                <p>
                  Il a reçu un email d'activation depuis <strong>veuillez-ne-pas-repondre@urssaf.fr</strong>.<br />
                  ➡️ Il doit ouvrir cet email et cliquer sur le lien d'activation.
                </p>
                <p>
                  ❗ <span className="font-medium">Mail introuvable ou expiré ?</span><br />
                  Il peut en demander un nouveau ici :{' '}
                  <a href="https://www.particulier.urssaf.fr/#/lien-activation-compte" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline font-medium">
                    particulier.urssaf.fr → lien d'activation
                  </a>
                </p>
              </div>
            </div>
          ) : (
            <p>
              Vous pouvez relancer <strong>{fullName}</strong> en lui envoyant un <strong>e-mail</strong> pour lui rappeler de finaliser son inscription.
            </p>
          )
        }
        valid="Relancer"
        cancel="Annuler"
        open={openRelance}
        handleClose={() => setOpenRelance(false)}
        handleSubmit={isPending ? handleRelance : () => handleRelance(client?.status)}
      />

      {/* ── Dialog : Déclarer (actif) ── */}
      <DialogLayout
        title="Déclaration d'un cours 🖍️"
        text={
          <div className="space-y-4">
            <p>Vous souhaitez déclarer un cours pour <strong>{fullName}</strong>.</p>
            <div className="flex gap-3 justify-center /flex-wrap">
              <ButtonField
                onClick={() => { handleRefreshAndDeclare(false); }}
                label={"📆 Cours ponctuel"}
                variant='outlined'
                className='w-full'
                loading={load && !typeCours}
              />
              <ButtonField
                onClick={() => { handleRefreshAndDeclare(true); }}
                label={"📚 Plusieurs cours"}
                variant='outlined'
                className='w-full'
                loading={load && typeCours}
              />
            </div>
          </div>
        }
        cancel="Annuler"
        open={openDeclare}
        load={load}
        handleClose={() => setOpenDeclare(false)}
        customBtnValid={<></>}
      />

      {/* ── Dialog : Archive/Désarchive ── */}
      <DialogLayout
        title={`Voulez-vous ${!isArchive ? 'archiver' : 'désarchiver'} ${getActiveClientBrandConfig('', 'cet ', 'ce ')} ?`}
        text={
          <p>
            En choisissant {!isArchive ? "d'archiver" : 'de désarchiver'}{' '}
            {getActiveClientBrandConfig('', "l'", 'le ')} <strong>{fullName}</strong>, celui-ci sera{' '}
            {!isArchive ? 'retiré de' : 'remis dans'} votre liste des {getActiveClientBrandConfig('s')} inscrits.
          </p>
        }
        valid={!isArchive ? 'Archiver' : 'Désarchiver'}
        cancel="Annuler"
        open={openArchive}
        load={load}
        handleClose={() => setOpenArchive(false)}
        handleSubmit={handleArchivage}
      />

      {/* ── Dialog : Suppression ── */}
      <DialogLayout
        title={`Voulez-vous supprimer ${getActiveClientBrandConfig('', 'cet ', 'ce ')} ?`}
        text={
          <p>
            Vous êtes sur le point de supprimer {getActiveClientBrandConfig('', "l'", 'le ')}{' '}
            <strong>{fullName}</strong>. Confirmez la suppression en cliquant sur <strong>Supprimer</strong>.
          </p>
        }
        valid="Supprimer"
        cancel="Annuler"
        open={openDelete}
        load={load}
        handleClose={() => setOpenDelete(false)}
        handleSubmit={isPending ? handleDeletePending : handleDeleteActive}
      />

      {/* ── Dialog : Profil (clients dont l'inscription n'est pas finalisée) ── */}
      <DialogLayout
        title={`Informations ${getActiveClientBrandConfig('', "de l'", 'du ')}`}
        maxWidth="sm"
        open={openProfile}
        customDialogActions="hidden"
        handleCloseIcon={() => setOpenProfile(false)}
      >
        <StudentInfoCard
          client={client}
          teachr={teachr}
          onRelance={client?.mail ? () => handleRelance(client?.status) : undefined}
        />
      </DialogLayout>
    </>
  );
};

export default ClientRowActions;
